import { useState } from "react";
import { toastService } from "@services/ToastService";
import { goalService } from "@services/GoalService";
import { GoalPayload } from "@shared/interfaces/ProgramFiles";

export const useGoal = () => {
  const [loading, setLoading] = useState(false);

  const saveGoal = async (
    payload: GoalPayload,
    options?: { goalId?: string }
  ) => {
    setLoading(true);
    try {
      const response = options?.goalId
        ? await goalService.updateGoal(options.goalId, payload)
        : await goalService.createGoal(payload);

      toastService.success(
        `Goal ${options?.goalId ? "updated" : "created"} successfully`
      );

      return response;
    } catch (error) {
      toastService.error("Failed to save goal");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getGoalById = async (goalId: string | number) => {
    try {
      return await goalService.getGoalById(goalId);
    } catch (error) {
      console.error("Failed to fetch goal");
      throw error;
    }
  };

  const deleteGoal = async (goalId: number | string) => {
    setLoading(true);
    try {
      await goalService.deleteGoal(goalId);
      toastService.success("Goal deleted");
    } catch (error) {
      toastService.error("Failed to delete goal");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchGoalsByProgram = async (
    programId: string,
    params?: { order?: string; offset?: number; limit?: number }
  ) => {
    setLoading(true);
    try {
      return await goalService.getGoalsByProgram(programId, params);
    } catch (error) {
      console.error("Failed to fetch goals");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramProjectsDropdown = async (
    programId: string,
    params?: { order?: string; offset?: number; limit?: number }
  ) => {
    setLoading(true);
    try {
      return await goalService.getProjectsInGoalsByProgram(programId, params);
    } catch (error) {
      console.error("Failed to fetch goals");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchGoalsDropdown = async (programId: string | number) => {
    try {
      return await goalService.getGoalsDropdown(programId);
    } catch (error) {
      console.error("Failed to fetch goals");
    }
  };

  return {
    loading,
    saveGoal,
    getGoalById,
    deleteGoal,
    fetchGoalsByProgram,
    fetchProgramProjectsDropdown,
    fetchGoalsDropdown,
  };
};
